import { FC, useEffect, useMemo, useRef, useState } from 'react';
import { Empty, Select, Spin } from 'antd';
import type { LabeledValue } from 'antd/lib/select';
import { _debounce } from '@medicusunion/front-utils';

/** Minimal row shape the picker needs — satisfied by both the patient and
 *  doctor list rows (id + name + email). */
export interface PickerUser {
  id: number;
  first_name: string;
  last_name: string;
  email: string;
}

interface IProps {
  // Search-capable fetch (e.g. fetchPatients / fetchDoctors with `search`).
  fetcher: (search: string) => Promise<PickerUser[]>;
  placeholder?: string;
  allowClear?: boolean;
  showEmail?: boolean; // include the email in the option label (default true)
  // labelInValue — antd Form passes these; also usable standalone.
  value?: LabeledValue;
  onChange?: (value?: LabeledValue) => void;
  // Extra: the full selected row (for prefilling other fields), or null.
  onSelectUser?: (user: PickerUser | null) => void;
}

/**
 * Searchable, debounced user picker (labelInValue so the chosen label
 * sticks even after the option list changes). Each instance owns its own
 * debounce, so multiple selects on one screen don't interfere.
 */
const AsyncUserSelect: FC<IProps> = ({
  fetcher,
  placeholder,
  allowClear,
  showEmail = true,
  value,
  onChange,
  onSelectUser
}) => {
  const debounce = useMemo(() => _debounce(), []);
  const [query, setQuery] = useState('');
  const [rows, setRows] = useState<PickerUser[]>([]);
  const [loading, setLoading] = useState(false);

  // Keep the latest fetcher without re-triggering the effect when a parent
  // passes a fresh inline function on every render.
  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        setLoading(true);
        const res = await fetcherRef.current(query);

        if (!cancelled) setRows(res);
      } catch {
        if (!cancelled) setRows([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [query]);

  const options = useMemo(
    () =>
      rows.map(row => ({
        value: row.id,
        label: `${row.first_name} ${row.last_name}${
          showEmail && row.email ? ` (${row.email})` : ''
        }`
      })),
    [rows, showEmail]
  );

  const handleChange = (next?: LabeledValue) => {
    onChange?.(next);

    if (!next) {
      onSelectUser?.(null);

      return;
    }

    onSelectUser?.(rows.find(row => row.id === next.value) ?? null);
  };

  return (
    <Select
      showSearch
      labelInValue
      filterOption={false}
      allowClear={allowClear}
      placeholder={placeholder}
      value={value}
      options={options}
      loading={loading}
      onChange={handleChange}
      onSearch={input => debounce(() => setQuery(input))}
      notFoundContent={
        loading ? (
          <Spin size="small" />
        ) : (
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
        )
      }
    />
  );
};

export default AsyncUserSelect;
